import React from 'react';
import { useEffect, useState } from "react";
import { 
    Card, 
    Typography, 
    Avatar, 
    Button, 
    Box, 
    CircularProgress, 
    Tooltip, 
    Stack, 
    useTheme,
    Pagination,
    Chip,
    Divider,
    IconButton
} from "@mui/joy";
import { useAuth } from "../context/AuthContext";
import { useMediaQuery } from "@mui/material";
import { useLanguage } from "../LanguageProvider";
import { getApiUrl } from '../utils/api';
import { 
    ShoppingCart, 
    CheckCircle, 
    Pending, 
    Cancel,
    AttachMoney,
    TrendingUp,
    ChevronLeft,
    ChevronRight,
    FirstPage,
    LastPage,
    BarChart,
    PieChart,
    ShowChart
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { 
    AnimatedBarChart, 
    AnimatedPieChart, 
    AnimatedLineChart,
    AnimatedAreaChart 
} from "../components/charts";
import ResponsivePieChart from '../components/charts/ResponsivePieChart';

function Orders() {
    const { user, loading } = useAuth();
    const [orders, setOrders] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [totals, setTotals] = useState({
        depositedApprovedSum: 0,
        otherStatusesSum: 0
    });
    const [loadingOrders, setLoadingOrders] = useState(true);
    const { currentLanguageData, language } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [dailySums, setDailySums] = useState(null);

    const fetchOrders = async (page = 1) => {
        setLoadingOrders(true);
        try {
            const response = await fetch(getApiUrl(`/v3/cloud/orders?page=${page}`), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.jwt_token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }

            const data = await response.json();
            setOrders(data.orders || []);
            setPagination(data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalItems: 0,
                itemsPerPage: 10,
                hasNextPage: false,
                hasPrevPage: false
            });
            setTotals(data.totals || {
                depositedApprovedSum: 0,
                otherStatusesSum: 0
            });
            setDailySums(data.dailySums || null);
        } catch (err) {
            console.log(err.message);
        } finally {
            setLoadingOrders(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchOrders();
        }
    }, [user]);

    const handlePageChange = (page) => {
        fetchOrders(page);
    };

    const getStatusIcon = (status) => {
        switch (status?.toUpperCase()) {
            case 'DEPOSITED':
            case 'APPROVED':
            case 'COMPLETED':
                return <CheckCircle color="success" />;
            case 'PENDING':
            case 'PROCESSING':
                return <Pending color="warning" />;
            case 'DECLINED':
            case 'CANCELLED':
            case 'FAILED':
                return <Cancel color="danger" />;
            default:
                return <Pending color="neutral" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'DEPOSITED':
            case 'APPROVED':
            case 'COMPLETED':
                return 'success';
            case 'PENDING':
            case 'PROCESSING':
                return 'warning';
            case 'DECLINED':
            case 'CANCELLED':
            case 'FAILED':
                return 'danger';
            default:
                return 'neutral';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        
        // Get the current language from the language context
        const currentLang = language || 'en';
        
        // Map language codes to locale codes
        const localeMap = {
            'en': 'en-US',
            'pl': 'pl-PL',
            'ru': 'ru-RU',
            'zh': 'zh-CN'
        };
        
        const locale = localeMap[currentLang] || 'en-US';
        
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount, currency = 'BYN') => {
        const formattedAmount = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
        return `${formattedAmount} ${currency}`;
    };

    // Chart data processing functions
    const processStatusData = () => {
        // Use the totals state instead of orders
        return Object.entries(totals).map(([status, count]) => ({
            label: status == 'otherStatusesSum' ? currentLanguageData.otherStatuses : currentLanguageData.deposited,
            value: count
        }));
    };

    const processWeeklyData = () => {
        const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        if (!dailySums) return [];
        return daysOrder.map(day => ({
            label: day.charAt(0).toUpperCase() + day.slice(1),
            value: dailySums[day] ?? 0
        }));
    };

    // Custom Pagination Component
    const CustomPagination = ({ currentPage, totalPages, onPageChange }) => {
        const getVisiblePages = () => {
            const delta = 2;
            const range = [];
            const rangeWithDots = [];

            for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
                range.push(i);
            }

            if (currentPage - delta > 2) {
                rangeWithDots.push(1, '...');
            } else {
                rangeWithDots.push(1);
            }

            rangeWithDots.push(...range);

            if (currentPage + delta < totalPages - 1) {
                rangeWithDots.push('...', totalPages);
            } else {
                rangeWithDots.push(totalPages);
            }

            return rangeWithDots;
        };

        const visiblePages = getVisiblePages();

        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 3 }}>
                {/* First Page Button */}
                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <IconButton
                        onClick={() => onPageChange(1)}
                        disabled={currentPage === 1}
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: currentPage === 1 ? 'transparent' : 'var(--joy-palette-primary-softBg)',
                            color: currentPage === 1 ? 'var(--joy-palette-neutral-500)' : 'var(--joy-palette-primary-softColor)',
                            '&:hover': {
                                backgroundColor: currentPage === 1 ? 'transparent' : 'var(--joy-palette-primary-softHoverBg)',
                            }
                        }}
                    >
                        <FirstPage />
                    </IconButton>
                </motion.div>

                {/* Previous Page Button */}
                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <IconButton
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: currentPage === 1 ? 'transparent' : 'var(--joy-palette-primary-softBg)',
                            color: currentPage === 1 ? 'var(--joy-palette-neutral-500)' : 'var(--joy-palette-primary-softColor)',
                            '&:hover': {
                                backgroundColor: currentPage === 1 ? 'transparent' : 'var(--joy-palette-primary-softHoverBg)',
                            }
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>
                </motion.div>

                {/* Page Numbers */}
                <Stack direction="row" spacing={0.5}>
                    <AnimatePresence mode="wait">
                        {visiblePages.map((page, index) => (
                            <motion.div
                                key={page}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                transition={{ duration: 0.2, delay: index * 0.05 }}
                            >
                                {page === '...' ? (
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--joy-palette-neutral-500)',
                                            fontSize: '14px',
                                            fontWeight: 500
                                        }}
                                    >
                                        ...
                                    </Box>
                                ) : (
                                    <motion.div
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <Button
                                            onClick={() => onPageChange(page)}
                                            variant={currentPage === page ? "solid" : "soft"}
                                            color="primary"
                                            sx={{
                                                minWidth: 40,
                                                height: 40,
                                                borderRadius: '12px',
                                                fontSize: '14px',
                                                fontWeight: currentPage === page ? 600 : 500,
                                                backgroundColor: currentPage === page 
                                                    ? 'var(--joy-palette-primary-500)' 
                                                    : 'var(--joy-palette-primary-softBg)',
                                                color: currentPage === page 
                                                    ? 'white' 
                                                    : 'var(--joy-palette-primary-softColor)',
                                                '&:hover': {
                                                    backgroundColor: currentPage === page 
                                                        ? 'var(--joy-palette-primary-600)' 
                                                        : 'var(--joy-palette-primary-softHoverBg)',
                                                }
                                            }}
                                        >
                                            {page}
                                        </Button>
                                    </motion.div>
                                )}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </Stack>

                {/* Next Page Button */}
                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <IconButton
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'var(--joy-palette-primary-softBg)',
                            color: currentPage === totalPages ? 'var(--joy-palette-neutral-500)' : 'var(--joy-palette-primary-softColor)',
                            '&:hover': {
                                backgroundColor: currentPage === totalPages ? 'transparent' : 'var(--joy-palette-primary-softHoverBg)',
                            }
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                </motion.div>

                {/* Last Page Button */}
                <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <IconButton
                        onClick={() => onPageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: currentPage === totalPages ? 'transparent' : 'var(--joy-palette-primary-softBg)',
                            color: currentPage === totalPages ? 'var(--joy-palette-neutral-500)' : 'var(--joy-palette-primary-softColor)',
                            '&:hover': {
                                backgroundColor: currentPage === totalPages ? 'transparent' : 'var(--joy-palette-primary-softHoverBg)',
                            }
                        }}
                    >
                        <LastPage />
                    </IconButton>
                </motion.div>
            </Box>
        );
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: ((window.__TAURI_INTERNALS__ && !isMobile) ? 'calc(100vh - 30px)' : '100vh') }}>
                <CircularProgress color='neutral' />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
            <Stack sx={{
                width: '90%',
                marginTop: '90px',
                maxWidth: '1200px',
            }}>
                {/* Header Card */}
                <Card variant="soft" color="primary" sx={{ mb: 2, overflow: 'hidden', borderRadius: 24 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <ShoppingCart />
                        <Typography level="title-lg">{currentLanguageData.orders || 'Orders'}</Typography>
                    </Stack>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                        {currentLanguageData.manageOrders || 'Manage and track your cloud service orders'}
                    </Typography>
                </Card>

                {/* Summary Cards */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 2 }}>
                    <Card variant="soft" color="success" sx={{ flex: 1, borderRadius: 24 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AttachMoney />
                            <Typography level="title-md">{currentLanguageData.approvedDeposits || 'Approved Deposits'}</Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.5, fontWeight: '600' }} level="h3">
                            {formatCurrency(totals.depositedApprovedSum, 'BYN')}
                        </Typography>
                    </Card>
                    
                    <Card variant="soft" color="danger" sx={{ flex: 1, borderRadius: 24 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <TrendingUp />
                            <Typography level="title-md">{currentLanguageData.canceledTransactions || 'Canceled Transactions'}</Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.5, fontWeight: '600' }} level="h3">
                            {formatCurrency(totals.otherStatusesSum, 'BYN')}
                        </Typography>
                    </Card>
                </Stack>

                {/* Charts Section (always visible) */}
                <Stack direction={isMobile ? 'column' : 'row'} spacing={3} sx={{ mb: 3 }}>
                    {/* Weekly Revenue Trend */}
                    <Card variant="soft" sx={{ borderRadius: 16, p: 3, flex: 1, minWidth: 0, borderRadius: 24 }}>
                        <AnimatedLineChart
                            data={processWeeklyData()}
                            color="#6366f1"
                            title={currentLanguageData.weeklyRevenue || 'Weekly Revenue Trend'}
                            subtitle={currentLanguageData.revenueOverWeek || 'Revenue over the last 7 days'}
                            style={{ flex: 1, minHeight: 250 }}
                        />
                    </Card>
                    {/* Order Status Distribution */}
                    <Card variant="soft" sx={{ borderRadius: 16, p: 3, flex: 1, minWidth: 0, borderRadius: 24 }}>
                        <ResponsivePieChart
                            data={processStatusData()}
                            innerRadius={0.4}
                            title={currentLanguageData.orderStatusDistribution || 'Order Status Distribution'}
                            subtitle={currentLanguageData.statusBreakdown || 'Breakdown by order status'}
                            style={{ flex: 1, minHeight: 250 }}
                        />
                    </Card>
                </Stack>

                {/* Orders List */}
                <Card variant="outlined" sx={{ mb: 2, borderRadius: 24 }}>
                    <Typography level="title-md">{currentLanguageData.orderHistory || 'Order History'}</Typography>
                    
                    {loadingOrders ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="neutral" />
                        </Box>
                    ) : orders.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography level="body-md" color="neutral">
                                {currentLanguageData.noOrdersFound || 'No orders found'}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Stack spacing={2}>
                                {orders.map((order, index) => (
                                    <motion.div
                                        key={order.id || index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.1 }}
                                    >
                                        <Card sx={{
                                            borderRadius: { xs: 10, sm: 20 },
                                            position: 'relative',
                                            p: { xs: 1.2, sm: 2 },
                                            mb: { xs: 1, sm: 2 },
                                            fontSize: { xs: '0.92rem', sm: '1rem' }
                                        }} variant="soft">
                                            <Stack spacing={2}>
                                                {/* Header Row */}
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Stack direction="row" alignItems="center" spacing={2}>
                                                        <Avatar 
                                                            src={order.user?.avatar} 
                                                            sx={{ width: 40, height: 40 }}
                                                        />
                                                        <Stack spacing={0.5}>
                                                            <Typography level="title-sm" fontWeight={600}>
                                                                {order.user?.usertag || currentLanguageData.unknownUser || 'Unknown User'}
                                                            </Typography>
                                                            <Typography level="body-xs" color="neutral">
                                                                {isMobile && order.user?.email && order.user.email.includes('@')
                                                                  ? (() => {
                                                                      const [name, domain] = order.user.email.split('@');
                                                                      return name.length > 3
                                                                        ? name.slice(0, 3) + '***@' + domain
                                                                        : name + '***@' + domain;
                                                                    })()
                                                                  : order.user?.email || currentLanguageData.noEmail || 'No email'}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                    <Chip 
                                                        size="sm" 
                                                        color={getStatusColor(order.status)}
                                                        startDecorator={getStatusIcon(order.status)}
                                                        sx={{ 
                                                            fontWeight: 600,
                                                            borderRadius: '12px',
                                                            px: 0.5
                                                        }}
                                                    >
                                                        {order.status || currentLanguageData.unknownStatus || 'Unknown'}
                                                    </Chip>
                                                </Stack>

                                                <Divider />

                                                {/* Order Details */}
                                                <Stack spacing={1.5} sx={{ flex: 1 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography level="title-md" fontWeight={600}>
                                                            {currentLanguageData.orderNumber || 'Order #'}{order.id}
                                                        </Typography>
                                                        <Chip 
                                                            size="sm" 
                                                            variant="soft" 
                                                            color="primary"
                                                            sx={{ borderRadius: '8px' }}
                                                        >
                                                            {order.type || currentLanguageData.unknownType || 'Unknown Type'}
                                                        </Chip>
                                                    </Stack>
                                                    
                                                    <Typography level="body-sm" color="neutral" sx={{ fontFamily: 'monospace' }}>
                                                        ID: {isMobile && order.order_id && order.order_id.length > 8
                                                          ? order.order_id.slice(0, 8) + '...'
                                                          : order.order_id || 'N/A'}
                                                    </Typography>
                                                    
                                                    <Typography level="body-xs" color="neutral">
                                                        {currentLanguageData.created || 'Created'}: {formatDate(order.created_at)}
                                                    </Typography>
                                                </Stack>
                                                
                                                {/* Amount positioned at bottom right */}
                                                <Box sx={{ 
                                                    position: 'absolute', 
                                                    bottom: 16, 
                                                    right: 16,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-end'
                                                }}>
                                                    <Typography level="h4" color="primary" fontWeight={700}>
                                                        {formatCurrency(order.amount, 'BYN')}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Card>
                                    </motion.div>
                                ))}
                            </Stack>
                            
                            {/* Custom Pagination */}
                            {pagination.totalPages > 1 && (
                                <CustomPagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </>
                    )}
                </Card>
            </Stack>
        </div>
    );
}

export default Orders; 